/**
 * 前后端共享 TS 类型（架构 §2 src/types/models.ts）
 *
 * 字段严格对齐 server 端：
 *  - models/user.model.ts        PublicUser
 *  - models/conversation.model.ts ConversationRow / MemberRow
 *  - models/message.model.ts      MessageRow
 *  - models/file.model.ts         AIFileRow / FileType
 *  - routes/invite.routes.ts      邀请码返回结构
 *  - routes/user.routes.ts        我的产出 myFiles
 *  - realtime/server.ts           实时事件信封 { type, payload }
 *
 * 注意：D1 返回的是 0/1 整数（is_ai_member / is_ai / is_revoked），
 * 前端以 `=== 1` 判定为真；如需布尔，见 helper isTrue()。
 */

export type Role = 'founder' | 'member' | 'ai';
export type ConversationType = 'dm' | 'group';
export type FileType = 'markdown' | 'table' | 'svg' | 'decision';
export type SenderType = 'user' | 'ai' | 'system';
export type MsgType = 'text' | 'image' | 'file' | 'system';

/** 公开用户信息（user.model.toPublic） */
export interface PublicUser {
  id: number;
  username: string;
  role: Role;
  is_active: boolean;
  created_at: string;
  /** 昵称（纯展示，可重复；null 表示未设置） */
  nickname: string | null;
  /** 头像 dataURL（上传后）；null 表示未设置，由 UserAvatar 兜底首字母 */
  avatar: string | null;
}

/** 会话（conversation.model.ConversationRow） */
export interface Conversation {
  id: number;
  type: ConversationType;
  name: string | null;
  created_by: number | null;
  is_ai_member: number; // 会话含 AI 参与者（群已拉知会 或 与知会私聊）
  /** U1：群级 AI 模式 quiet|partner|host，默认 quiet */
  ai_mode?: string | null;
  /** U2：建群时携带的项目模板 id（std-product/research/content），无则 null */
  template_id?: string | null;
  created_at: string;
  /** 列表预览：末条消息（后端 listForUserWithLast 返回，CP13） */
  last_message?: string | null;
  last_message_at?: string | null;
  last_message_sender_id?: number | null;
  last_message_sender_type?: SenderType | null;
  last_message_id?: number | null;
  /** 当前用户是否置顶（CP14） */
  pinned?: number;
  /** 当前用户已读到的最后消息 id（CP14 未读基准） */
  last_read_message_id?: number;
  /** 未读消息数（CP14，后端 unread_count） */
  unread_count?: number;
  /** 搜索命中片段（CP14，仅搜索接口返回） */
  match_snippet?: string | null;
}

/** 会话成员（conversation.model.MemberRow） */
export interface Member {
  conversation_id: number;
  user_id: number;
  /** 用户全局角色（founder/member），由后端 JOIN User 返回（注意：非会话内角色） */
  role: 'owner' | 'member' | 'ai';
  /** 用户在会话内的角色（owner/member/ai），由后端 listMembers 以 member_role 返回；群主判定用此字段 */
  member_role?: 'owner' | 'member' | 'ai';
  is_ai: number; // 0/1
  joined_at: string;
  /** 后端 listMembers 经 JOIN User 返回的对端用户名（前端 DM 列表推导对端时使用） */
  username?: string;
}

/** AI 产出文件（file.model.AIFileRow） */
export interface AIFile {
  id: number;
  message_id: number;
  conversation_id: number;
  owner_user_id: number | null;
  file_type: FileType;
  content_text: string;
  original_name: string;
  mime: string;
  size: number;
  created_at: string;
}

/** 消息（message.model.MessageRow + 路由附带的 files） */
export interface Message {
  id: number;
  conversation_id: number;
  sender_id: number | null;
  sender_type: SenderType;
  content: string;
  msg_type: MsgType;
  has_image: number; // 0/1
  attachment_data: string | null; // 小图 dataURL（v1 展示）
  attachment_url: string | null;
  reply_to: number | null;
  created_at: string;
  files?: AIFile[]; // 历史/实时消息附带，AI 产出文件卡片
  /** 用户附件元数据 JSON（[{ext,name,dataUrl}]），展示与刷新保留（BUG-010） */
  attachments?: string | null;
}

/** 我的产出（user.routes /user/me 的 myFiles） */
export interface MyFile {
  id: number;
  file_type: FileType;
  original_name: string;
  mime: string;
  size: number;
  conversation_id: number;
  created_at: string;
  download_url: string;
}

/** 用户统计（/user/me 的 stats） */
export interface UserStats {
  messageCount: number;
  sessionCount: number;
  fileCount: number;
  loginCount: number;
  lastLoginAt: string | null;
  joinDate: string;
}

/** 活动类型（/user/activity） */
export type ActivityType = 'message' | 'aifile' | 'join' | 'login';

/** 活动流单项（/user/activity） */
export interface ActivityItem {
  type: ActivityType;
  at: string;
  cid: number | null;
  summary: string | null;
}

/** 用户搜索结果（/user/lookup 扩展） */
export interface LookupUser {
  id: number;
  username: string;
  nickname: string | null;
  avatar: string | null;
  role: Role;
}

/** 当前用户（/user/me 的 user，含统计） */
export interface MeUser extends PublicUser {
  stats: UserStats;
}

/** /user/me 响应 */
export interface MeResponse {
  user: MeUser;
  myFiles: MyFile[];
}

/** /user/activity 响应 */
export interface ActivityResponse {
  items: ActivityItem[];
  total: number;
  page: number;
  size: number;
}

/** 邀请码（invite.routes 列表/创建/更新返回） */
export interface Invite {
  id: number;
  code: string;
  created_by: number;
  used_count: number;
  max_uses: number | null;
  expires_at: string | null;
  is_revoked: number; // 0/1
  created_at: string;
}

/** 统一响应信封（§7.2 { code, data, message }） */
export interface ApiEnvelope<T> {
  code: number;
  data: T;
  message: string;
}

/** 实时事件信封：PartyKit 广播 JSON.stringify({ type, payload })（§7.3） */
export interface RealtimeEvent<P = unknown> {
  type: string;
  payload: P;
}

/** 把 0/1 整数安全转为布尔 */
export function isTrue(v: number | null | undefined): boolean {
  return v === 1;
}

/** 是否为"与知会私聊"（AI 一对一会话：含 AI 参与者且类型为 dm） */
export function isAiDm(conv?: Conversation | null): boolean {
  return !!conv && isTrue(conv.is_ai_member) && conv.type === 'dm';
}

/** 错误码（§7.2） */
export const ErrorCode = {
  OK: 0,
  AUTH_INVALID_TOKEN: 1001,
  AUTH_EXPIRED: 1002,
  AUTH_WRONG_PASSWORD: 1003,
  INVITE_INVALID: 2001,
  INVITE_USED_UP: 2002,
  AI_KEY_MISSING: 3001,
  AI_TIMEOUT: 3002,
  AI_MODEL_UNAVAILABLE: 3003,
  FILE_NOT_FOUND: 4001,
  // U1 新增（架构 §7.2；原 4002/4003 已被 USER_NOT_FOUND/FORBIDDEN 占用）
  INVALID_MODE: 4004,
  INVALID_STATE_TRANSITION: 4005,
  INVALID_ARTIFACT_TYPE: 4006,
  ARTIFACT_NOT_FOUND: 4007,
  ARTIFACT_FORBIDDEN: 4008,
  // U2 新增
  MEMORY_NOT_FOUND: 4009,
  TEMPLATE_INVALID: 4010,
  SERVER_ERROR: 5000,
} as const;

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode];

/** 鉴权结果（auth.service 返回 { token, user }） */
export interface AuthResult {
  token: string;
  user: PublicUser;
}

/** 附件输入（message:send 携带；dataURL 数组） */
export interface AttachmentInput {
  ext?: string;
  dataUrl?: string;
  name?: string;
}

// ============================================================
// U1：结构化成果（Artifact）共享类型（对齐 server artifact.model.ts）
// ============================================================

/** 群级 AI 模式 */
export type AiMode = 'quiet' | 'partner' | 'host';

/** 成果类型：议题 / 方案 / 决策 / 行动 */
export type ArtifactType = 'issue' | 'solution' | 'decision' | 'action';

/** 成果状态：草稿 / 待确认 / 已确认 */
export type ArtifactState = 'draft' | 'pending' | 'confirmed';

/** 结构化成果（artifact.model.ArtifactRow），fields 为 JSON 字符串 */
export interface Artifact {
  id: number;
  conversation_id: number;
  type: ArtifactType;
  state: ArtifactState;
  title: string;
  /** JSON 字符串，按 type 的结构化业务字段 */
  fields: string;
  owner_id: number | null;
  confirmed_by: number | null;
  confirmed_at: string | null;
  parent_artifact_id: number | null;
  /** JSON 数组字符串：[message_id, ...]（OQ4，来源消息，本地派生 #N） */
  source_message_ids: string | null;
  ai_generated: number;
  message_id: number | null;
  file_id: number | null;
  version: number;
  created_at: string;
  updated_at: string;
}

/** 成果版本快照（artifact.model.ArtifactVersionRow） */
export interface ArtifactVersion {
  id: number;
  artifact_id: number;
  version: number;
  title: string;
  fields: string;
  editor_id: number | null;
  created_at: string;
}

/** AI 主动提示（realtime ai:notice 事件载荷） */
export interface AiNotice {
  id: string;
  conversation_id: number;
  mode: AiMode;
  title: string;
  summary: string;
  suggestions: string[];
  created_at: string;
}

// ============================================================
// U2：项目记忆 / 周摘要 / 模板 共享类型（对齐 server）
// ============================================================

/** 语言（与后端 Locale 保持一致） */
export type Locale = 'zh' | 'en';

/** 项目记忆（server project_memory 行） */
export interface ProjectMemory {
  id: number;
  conversation_id: number;
  content: string;
  source_type: string; // 'manual' | 'auto'
  created_by: number | null;
  created_at: string;
}

/** 周摘要（server weekly_digest 行） */
export interface WeeklyDigest {
  id: number;
  conversation_id: number;
  week_start: string;
  week_end: string;
  generated_by: number | null;
  locale: string;
  content_text: string;
  created_at: string;
}

/** 模板骨架项（与后端 TemplateSkeletonItem 对齐） */
export interface TemplateSkeletonItem {
  type: 'issue' | 'solution' | 'decision' | 'action';
  title: string;
  fields: Record<string, unknown>;
}

/** 项目模板（与后端 ProjectTemplate 对齐） */
export interface ProjectTemplate {
  id: string;
  name: Record<Locale, string>;
  default_ai_mode: AiMode;
  skeleton: TemplateSkeletonItem[];
}
